import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Cog6ToothIcon, InformationCircleIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { getThreeCircles } from '../../../services/firebase/threeCircles';
import type { ThreeCirclesConfig } from '../../../types/threeCircles';

const MAX_VISIBLE = 5;

// ── Circle definitions ────────────────────────────────────────────────────────

const CIRCLES = [
  {
    key: 'outerCircle' as const,
    label: 'Outer Circle',
    badge: 'Healthy',
    headerColor: 'text-emerald-600 dark:text-emerald-400',
    badgeBg: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300',
    borderColor: 'border-emerald-200 dark:border-emerald-800',
    dotColor: 'bg-emerald-400',
    description:
      'Healthy behaviors that support your recovery — things you commit to doing regularly to maintain your spiritual condition.',
  },
  {
    key: 'middleCircle' as const,
    label: 'Middle Circle',
    badge: 'Caution',
    headerColor: 'text-amber-600 dark:text-amber-400',
    badgeBg: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
    borderColor: 'border-amber-200 dark:border-amber-800',
    dotColor: 'bg-amber-400',
    description:
      'Warning signs and behaviors that could lead toward relapse. Not acting out, but getting close to the edge.',
  },
  {
    key: 'innerCircle' as const,
    label: 'Inner Circle',
    badge: 'Bottom Line',
    headerColor: 'text-red-600 dark:text-red-400',
    badgeBg: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
    borderColor: 'border-red-200 dark:border-red-800',
    dotColor: 'bg-red-400',
    description:
      'Bottom-line behaviors you are fully committed to abstaining from. If you cross this line, reach out for help immediately.',
  },
] as const;

// ── Info popover ──────────────────────────────────────────────────────────────

function InfoPopover({ description }: { description: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div ref={ref} className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="text-surface-400 hover:text-surface-600 dark:hover:text-surface-300 transition-colors"
        aria-label="What is this circle?"
      >
        <InformationCircleIcon className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute left-0 top-6 z-50 w-56 bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-xl shadow-lg p-3 text-xs text-surface-600 dark:text-surface-300 leading-relaxed">
          {description}
        </div>
      )}
    </div>
  );
}

// ── Column ────────────────────────────────────────────────────────────────────

interface ColumnProps {
  circle: (typeof CIRCLES)[number];
  items: string[];
  showAll: boolean;
}

function CircleColumn({ circle, items, showAll }: ColumnProps) {
  const visibleItems = showAll ? items : items.slice(0, MAX_VISIBLE);

  return (
    <div className={`md:flex-1 rounded-xl border ${circle.borderColor} flex flex-col overflow-hidden`}>
      {/* Column header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-surface-100 dark:border-surface-700">
        <div className="flex items-center gap-1.5">
          <span className={`text-xs font-semibold ${circle.headerColor}`}>{circle.label}</span>
          <InfoPopover description={circle.description} />
        </div>
        <div className="flex items-center gap-1.5">
          {items.length > 0 && (
            <span className={`text-[10px] font-semibold tabular-nums ${circle.headerColor} opacity-70`}>
              {items.length}
            </span>
          )}
          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${circle.badgeBg}`}>
            {circle.badge}
          </span>
        </div>
      </div>

      {/* Item list */}
      <div className="p-2 space-y-1">
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center px-2 py-3">
            <p className="text-xs text-surface-400 dark:text-surface-500 mb-1">Not set up yet.</p>
            <Link
              to="/settings/three-circles"
              className="text-xs text-teal-600 dark:text-teal-400 underline"
            >
              Configure →
            </Link>
          </div>
        ) : (
          visibleItems.map((item, i) => (
            <div key={i} className="flex items-start gap-1.5">
              <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${circle.dotColor}`} />
              <span className="text-xs text-surface-700 dark:text-surface-300 leading-relaxed">
                {item}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ── Card ──────────────────────────────────────────────────────────────────────

interface ThreeCirclesCardProps {
  uid: string;
}

export function ThreeCirclesCard({ uid }: ThreeCirclesCardProps) {
  const [config, setConfig] = useState<ThreeCirclesConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    getThreeCircles(uid)
      .then(setConfig)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [uid]);

  if (loading || !config || !config.enabled) return null;

  const hasMore = CIRCLES.some(c => (config[c.key] ?? []).length > MAX_VISIBLE);

  return (
    <div className="bg-white dark:bg-surface-800 rounded-xl border border-surface-200 dark:border-surface-700 p-4">
      {/* Card header */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-surface-900 dark:text-surface-100">
            Three Circles
          </h3>
          <p className="text-xs text-surface-500 dark:text-surface-400">Recovery Boundaries</p>
        </div>
        <Link
          to="/settings/three-circles"
          className="p-1.5 rounded-lg text-surface-400 hover:text-teal-600 dark:hover:text-teal-400 hover:bg-surface-100 dark:hover:bg-surface-700 transition-colors"
          aria-label="Three Circles settings"
        >
          <Cog6ToothIcon className="w-4 h-4" />
        </Link>
      </div>

      {/* Circles — stacked on mobile, side-by-side on md+ */}
      <div className="flex flex-col gap-2 md:flex-row">
        {CIRCLES.map(circle => (
          <CircleColumn
            key={circle.key}
            circle={circle}
            items={config[circle.key] ?? []}
            showAll={showAll}
          />
        ))}
      </div>

      {/* Show all toggle */}
      {hasMore && (
        <button
          type="button"
          onClick={() => setShowAll(v => !v)}
          className="mt-2 flex items-center gap-1 text-xs text-surface-500 dark:text-surface-400 hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
        >
          <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${showAll ? 'rotate-180' : ''}`} />
          {showAll ? 'Show less' : 'Show all'}
        </button>
      )}
    </div>
  );
}
